import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

function StarDisplay({ rating, size = 13 }) {
  if (!rating) return <span style={{ fontSize: size, color: '#b0a090' }}>Sin rating</span>;
  const r = Math.round(parseFloat(rating));
  return (
    <span style={{ color: '#f59e0b', fontSize: size }}>
      {'★'.repeat(r)}{'☆'.repeat(5 - r)}
      <span style={{ color: '#7a7060', fontSize: size - 1, marginLeft: 4 }}>
        {parseFloat(rating).toFixed(1)}
      </span>
    </span>
  );
}

function DeliveryBadge({ method }) {
  const icons = { cash: '💵', transfermovil: '📱', cubacel: '📶' };
  return (
    <span style={{ padding: '2px 8px', borderRadius: 20, background: '#f0ebe2', fontSize: 11, fontWeight: 600, color: '#7a7060' }}>
      {icons[method] || '📦'} {method?.replace('_', ' ')}
    </span>
  );
}

export default function AgentMarketplace() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taking, setTaking] = useState(null);
  const [filter, setFilter] = useState('all');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [takenOrders, setTakenOrders] = useState(new Set());

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/marketplace/orders');
      setOrders(Array.isArray(res.data) ? res.data : []);
      setLastRefresh(new Date());
    } catch { setOrders([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [load]);

  const takeOrder = async (ref) => {
    setTaking(ref);
    try {
      const res = await api.post(`/api/v1/marketplace/orders/${ref}/take`);
      setTakenOrders(prev => new Set([...prev, ref]));
      setOrders(prev => prev.filter(o => o.ref !== ref));
      alert(`✅ Orden ${ref} tomada. ${res.data.message}`);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al tomar la orden');
    } finally { setTaking(null); }
  };

  const filtered = orders.filter(o =>
    filter === 'all' || o.delivery_method === filter
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: 'Georgia,serif', fontSize: 26, margin: '0 0 4px' }}>Mercado de órdenes</h1>
          <p style={{ fontSize: 13, color: '#7a7060', margin: 0 }}>
            {filtered.length} órdenes disponibles · Auto-refresh 15s · {lastRefresh.toLocaleTimeString('es-ES')}
          </p>
        </div>
        <button onClick={load} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2dbd0', background: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
          ↻ Actualizar
        </button>
      </div>

      {/* Filter by delivery method */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['all', 'Todas'], ['cash', '💵 Efectivo'], ['transfermovil', '📱 Transfermóvil'], ['cubacel', '📶 Cubacel']].map(([f, l]) => (
          <button key={f} onClick={() => setFilter(f)} style={{ padding: '7px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: filter === f ? '#c94a1e' : '#f0ebe2', color: filter === f ? '#fff' : '#7a7060' }}>
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#7a7060' }}>Cargando órdenes...</div>
      ) : filtered.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 14, padding: 48, textAlign: 'center', border: '1px solid #e2dbd0' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 18, marginBottom: 8 }}>Sin órdenes disponibles</div>
          <div style={{ fontSize: 13, color: '#7a7060' }}>Las nuevas órdenes aparecen aquí automáticamente</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {filtered.map((order, i) => (
            <div key={i} style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: '20px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#c94a1e', marginBottom: 4 }}>{order.ref}</div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{order.recipient_name}</div>
                <div style={{ fontSize: 13, color: '#7a7060', marginBottom: 8 }}>{order.phone_preview || order.recipient_phone}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <DeliveryBadge method={order.delivery_method} />
                  <span style={{ fontSize: 11, color: '#b0a090' }}>
                    {order.created_at ? new Date(order.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '—'}
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'Georgia,serif', fontSize: 28, fontWeight: 700, color: '#2d6a4f' }}>
                    ${order.net_usd || order.amount_usd}
                  </div>
                  <div style={{ fontSize: 11, color: '#7a7060' }}>a entregar</div>
                </div>
                <button
                  onClick={() => takeOrder(order.ref)}
                  disabled={taking === order.ref || takenOrders.has(order.ref)}
                  style={{
                    padding: '10px 20px', borderRadius: 8, border: 'none',
                    background: taking === order.ref ? '#e2dbd0' : '#c94a1e',
                    color: '#fff', fontSize: 13, fontWeight: 700,
                    cursor: taking === order.ref ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                  }}>
                  {taking === order.ref ? 'Tomando...' : '→ Tomar orden'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
